// MIT License - Copyright (c) 2026 Doug McNall

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { TRAINING_PROGRAMS } from '../config/CrewTrainingConfig';
import CrewTrainingManager from '../utils/game/CrewTrainingManager';
import { useCredits } from '../contexts/CreditsContext';
import './CrewTrainingModal.css';

const CrewTrainingModal = ({ isOpen, crewMember, onClose, onTrainingStarted }) => {
  const { credits, removeCredits } = useCredits();
  const [activeTraining, setActiveTraining] = useState(null);
  const [completedPrograms, setCompletedPrograms] = useState([]);

  useEffect(() => {
    if (isOpen && crewMember) {
      const training = CrewTrainingManager.getActiveTraining(crewMember.id);
      const completed = CrewTrainingManager.getCompletedTraining(crewMember.id);
      setActiveTraining(training);
      setCompletedPrograms(completed);
    }
  }, [isOpen, crewMember]);

  const handleEnroll = (programId) => {
    const result = CrewTrainingManager.enrollInTraining(
      crewMember.id,
      programId,
      { removeCredits, canAfford: (cost) => credits >= cost }
    );

    if (result.success) {
      setActiveTraining(result.session);
      if (onTrainingStarted) onTrainingStarted(result.message);
    } else {
      alert(result.message);
    }
  };

  const getEligibility = (programId) => {
    return CrewTrainingManager.canEnrollInTraining(crewMember.id, programId);
  };

  if (!isOpen || !crewMember) return null;

  return createPortal(
    <div className="crew-training-modal-overlay" onClick={onClose}>
      <div className="crew-training-modal" onClick={(e) => e.stopPropagation()}>
        <div className="training-header">
          <h2>🎓 TRAINING PROGRAMS</h2>
          <p className="training-subtitle">{crewMember.name} - {crewMember.role}</p>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>

        {activeTraining && (
          <div className="active-training-banner">
            <div className="banner-content">
              <span className="banner-icon">{activeTraining.program.icon}</span>
              <div className="banner-text">
                <strong>Currently Training: {activeTraining.program.name}</strong>
                <div className="progress-bar-container">
                  <div 
                    className="progress-bar-fill" 
                    style={{ width: `${activeTraining.progress * 100}%` }}
                  />
                </div>
                <span className="progress-text">{Math.round(activeTraining.progress * 100)}% Complete</span>
              </div>
            </div>
          </div>
        )}

        <div className="training-programs-grid">
          {Object.values(TRAINING_PROGRAMS).map(program => {
            const eligibility = getEligibility(program.id);
            const isCompleted = completedPrograms.some(c => c.programId === program.id);
            const canAfford = credits >= program.cost;

            return (
              <div 
                key={program.id} 
                className={`training-card ${!eligibility.canEnroll ? 'locked' : ''} ${isCompleted ? 'completed' : ''}`}
              >
                <div className="training-card-header">
                  <span className="training-icon">{program.icon}</span>
                  <h3>{program.name}</h3>
                  {isCompleted && <span className="completed-badge">✓</span>}
                </div>

                <p className="training-description">{program.description}</p>

                <div className="training-details">
                  <div className="detail-row">
                    <span className="detail-label">Duration:</span>
                    <span className="detail-value">{program.duration} days</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Cost:</span>
                    <span className={`detail-value ${!canAfford ? 'insufficient' : ''}`}>
                      {program.cost} CR
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">XP Reward:</span>
                    <span className="detail-value">+{program.rewards.xp} XP</span>
                  </div>
                </div>

                <div className="training-bonuses">
                  <strong>Skill Bonuses:</strong>
                  <ul>
                    {Object.entries(program.rewards.skillBonuses).map(([skill, bonus]) => (
                      <li key={skill}>
                        +{Math.round(bonus * 100)}% {skill.replace(/([A-Z])/g, ' $1').trim()}
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  className="enroll-button"
                  disabled={!eligibility.canEnroll || activeTraining || !canAfford}
                  onClick={() => handleEnroll(program.id)}
                >
                  {!eligibility.canEnroll ? eligibility.reason :
                   activeTraining ? 'Training in Progress' :
                   !canAfford ? 'Insufficient Credits' :
                   isCompleted ? 'Retake Training' : 'Enroll'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CrewTrainingModal;
